import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaBell, FaBriefcase, FaFileInvoiceDollar, FaCheckCircle, FaPlay, FaStop, FaExclamationTriangle } from 'react-icons/fa';
import { getApiEndpoint } from '../../config/api';
import './shared.css';

const ICON_MAP = {
  job_invite: FaBriefcase,
  job_assigned: FaBriefcase,
  quote_received: FaFileInvoiceDollar,
  quote_approved: FaCheckCircle,
  quote_declined: FaExclamationTriangle,
  job_started: FaPlay,
  job_ended: FaStop,
  conflict: FaExclamationTriangle,
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const token = localStorage.getItem('token');

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(getApiEndpoint('/api/notifications?limit=20'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (id) => {
    try {
      await fetch(getApiEndpoint(`/api/notifications/${id}/read`), {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await fetch(getApiEndpoint('/api/notifications/read-all'), {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  };

  const timeAgo = (ts) => {
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="cs-notif-wrapper" ref={ref}>
      <button className="cs-notif-bell" onClick={() => setOpen(!open)} aria-label="Notifications">
        <FaBell />
        {unreadCount > 0 && <span className="cs-notif-count">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {open && (
        <div className="cs-notif-dropdown">
          <div className="cs-notif-header">
            <span>Notifications</span>
            {unreadCount > 0 && (
              <button className="cs-notif-mark-all" onClick={markAllRead}>Mark all read</button>
            )}
          </div>
          <div className="cs-notif-list">
            {notifications.length === 0 ? (
              <div className="cs-notif-empty">No notifications</div>
            ) : (
              notifications.map(n => {
                const Icon = ICON_MAP[n.type] || FaBell;
                return (
                  <div
                    key={n.id}
                    className={`cs-notif-item ${!n.is_read ? 'unread' : ''}`}
                    onClick={() => !n.is_read && markRead(n.id)}
                  >
                    <div className="cs-notif-icon"><Icon /></div>
                    <div className="cs-notif-body">
                      <span className="cs-notif-title">{n.title}</span>
                      <span className="cs-notif-msg">{n.message}</span>
                      <span className="cs-notif-time">{timeAgo(n.created_at)}</span>
                    </div>
                    {!n.is_read && <span className="cs-notif-dot" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
